import React, { useEffect, useState } from 'react'
import { Box } from '@mui/system'
import { Typography, Card, CardContent, Avatar } from '@mui/material'
import Sidebar from './sidebar'

import { useNavigate } from 'react-router-dom'
import "../App.css"

import { collection, query, getDocs } from "firebase/firestore";
import { db } from "../firebase.js";

import { genderProject, isWithinLastDays } from '../utils/reportUtils'

import PeopleIcon from '@mui/icons-material/People'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import HomeIcon from '@mui/icons-material/Home'
import BarChartIcon from '@mui/icons-material/BarChart'
import RestaurantIcon from '@mui/icons-material/Restaurant'
import ShowerIcon from '@mui/icons-material/Shower'
import DescriptionIcon from '@mui/icons-material/Description'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import AssessmentIcon from '@mui/icons-material/Assessment'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import FavoriteIcon from '@mui/icons-material/Favorite'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

const GREEN = "#8DBF41"
const MOSES_BLUE = "#0CACEC"
const MIRIAM_PURPLE = "#3a103e"

const projectCards = [
  { key: "moses", title: "Moses Project", color: MOSES_BLUE, bg: "#f2f9fd" },
  { key: "miriam", title: "Miriam Project", color: MIRIAM_PURPLE, bg: "#f7f4f7" },
]

const reports = [
  { path: "/services", title: "Daily Submissions Report", description: "View a summary of services provided.", icon: <CalendarMonthIcon /> },
  { path: "/homelessnessReport", title: "Homelessness Report", description: "View homelessness support data.", icon: <HomeIcon /> },
  { path: "/crimeStatsReport", title: "Crime Stats Report", description: "View crime related statistics.", icon: <BarChartIcon /> },
  { path: "/foodReport", title: "Food Report", description: "View food provision data.", icon: <RestaurantIcon /> },
  { path: "/hygieneReport", title: "Hygiene Report", description: "View hygiene support data.", icon: <ShowerIcon /> },
  { path: "/referralReport", title: "Referral Report", description: "View referral source data and breakdowns.", icon: <PeopleIcon /> },
  { path: "/correspondenceReport", title: "Correspondence Type Report", description: "View and analyse correspondence types.", icon: <DescriptionIcon /> },
  { path: "/caseStudyReport", title: "Case Study", description: "Search a beneficiary and view their case studies.", icon: <MenuBookIcon /> },
  { path: "/dailyRegisterReport", title: "Daily Register Report", description: "View attendance totals and per-person occasions.", icon: <AssessmentIcon /> },
  { path: "/activityLogViewer", title: "View Activity Logs", description: "View all information relating to daily activities and attendance.", icon: <AccessTimeIcon /> },
  { path: "/currentlyinRehab", title: "Currently in Rehab", description: "View current beneficiaries in rehab.", icon: <FavoriteIcon /> },
]

export default function Home() {
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    moses: { 30: 0, 90: 0, 365: 0 },
    miriam: { 30: 0, 90: 0, 365: 0 },
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    const querySnapshot = await getDocs(query(collection(db, "employee")));
    const next = {
      moses: { 30: 0, 90: 0, 365: 0 },
      miriam: { 30: 0, 90: 0, 365: 0 },
    };
    querySnapshot.forEach((document) => {
      const e = document.data();
      const project = genderProject(e.gender) === "Miriam" ? "miriam" : "moses";
      [30, 90, 365].forEach((days) => {
        if (isWithinLastDays(e.startDate, days)) next[project][days] += 1;
      });
    });
    setStats(next);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar />

      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 4 }, bgcolor: "#f4f6f8", minHeight: "100vh" }}>
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>Beneficiary Numbers</Typography>
            <Typography sx={{ color: "text.secondary" }}>A snapshot of recent beneficiaries started for each project.</Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 4 }}>
            {projectCards.map((c) => (
              <Box
                key={c.key}
                sx={{
                  flex: "1 1 380px",
                  bgcolor: c.bg,
                  borderLeft: `8px solid ${c.color}`,
                  borderRadius: 2,
                  p: 2.5,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                }}
              >
                <Box
                  onClick={() => navigate(`/viewAll?project=${c.key}`)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    width: "fit-content",
                    cursor: "pointer",
                    "&:hover .project-title": { textDecoration: "underline" },
                  }}
                >
                  <Avatar sx={{ bgcolor: c.color, width: 48, height: 48 }}>
                    <PeopleIcon />
                  </Avatar>
                  <Typography className="project-title" variant="h5" sx={{ fontWeight: 700, color: c.color }}>
                    {c.title}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", mt: 2 }}>
                  {[30, 90, 365].map((days, i) => (
                    <Box
                      key={days}
                      sx={{
                        flex: 1,
                        textAlign: "center",
                        borderLeft: i > 0 ? "1px solid #dfe6ea" : "none",
                      }}
                    >
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>{stats[c.key][days]}</Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>Last {days} days</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 700 }}>Reports</Typography>
          <Typography sx={{ color: "text.secondary", mb: 2 }}>Generate and view reports from your data.</Typography>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>
            {reports.map((r) => (
              <Card
                key={r.path}
                onClick={() => navigate(r.path)}
                sx={{
                  cursor: "pointer",
                  border: "1px solid #e3e9ee",
                  borderRadius: 2,
                  boxShadow: "none",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    boxShadow: "0 6px 16px rgba(0,0,0,0.10)",
                    transform: "translateY(-2px)",
                    borderColor: GREEN,
                  },
                }}
              >
                <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, "&:last-child": { pb: 2 } }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      bgcolor: GREEN,
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {r.icon}
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.25 }}>{r.title}</Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>{r.description}</Typography>
                  </Box>
                  <ChevronRightIcon sx={{ color: GREEN }} />
                </CardContent>
              </Card>
            ))}
          </Box>

        </Box>
      </Box>
    </Box>
  )
}
